import React, { useState, useEffect } from 'react';
import CommonModal from './CommonModal';
import { 
  GetStateType, 
  CreateClientContact, 
  UpdateClientContact,
  GetClientContact,
  GetTimezoneType
} from '../../services/accountService';

const ContactModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  contact = null, 
  mode = 'add',
  clientId 
}) => {
  const [stateOptions, setStateOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingTimezones, setLoadingTimezones] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);
  const [error, setError] = useState('');
  const [formInitialized, setFormInitialized] = useState(false);
  const [initialFormData, setInitialFormData] = useState({
    firstName: '',
    lastName: '',
    jobTitle: '',
    timeZone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });
  
  const isEditMode = mode === 'edit';
  
  // Fetch states and timezones when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setFormInitialized(false);
      fetchStates();
      fetchTimezones();
    }
  }, [isOpen]);

  // Initialize form data after options are loaded
  useEffect(() => {
    if (isOpen && !formInitialized) {
      if (isEditMode && contact) {
        const formData = {
          firstName: contact.firstName || contact.contactFirstName || '',
          lastName: contact.lastName || contact.contactLastName || '',
          jobTitle: contact.jobTitle || contact.contactPosition || '',
          timeZone: contact.timeZoneID?.toString() || contact.timeZone || '',
          email: contact.email || contact.contactEmail || '',
          address: contact.address || contact.contactAddress || '',
          city: contact.city || contact.contactCity || '',
          state: contact.state || contact.contactState || '',
          zipCode: contact.zipCode || contact.contactZipCode || '',
          phone: contact.phone || contact.contactPhone || ''
        };
        setInitialFormData(formData);
        setFormInitialized(true);
      } else {
        initializeFormData();
      }
    }
  }, [isOpen, formInitialized, isEditMode, contact]);

  // FINAL FIX: Bind state after stateOptions load using contactStateID
  useEffect(() => {
    if (
      isEditMode &&
      contact?.contactStateID &&
      stateOptions.length > 0
    ) {
      const stateAbbr = getStateAbbreviation(contact.contactStateID);
      if (stateAbbr) {
        setInitialFormData(prev => ({
          ...prev,
          state: stateAbbr
        }));
      }
    }
  }, [stateOptions, isEditMode, contact]);

  // Fetch states from API
  const fetchStates = async () => {
    try {
      setLoadingStates(true);
      const response = await GetStateType();
      
      if (response.success && response.data && Array.isArray(response.data)) {
        const transformedStates = response.data.map(state => {
          const fullName = state.stateName;
          // Truncate to 15 chars with ellipsis if longer
          const displayLabel = fullName.length > 12
            ? fullName.slice(0, 12) + '...' 
            : fullName;
          
          return {
            value: state.stateAbbreviation,
            label: displayLabel,        // truncated for display
            title: fullName,            // full text shown on hover (tooltip)
            fullLabel: fullName,        // keep full version if needed
            stateId: state.stateID || state.id
          };
        });
        
        setStateOptions([
          ...transformedStates
        ]);
      } else {
        console.warn("State API response structure unexpected or empty");
        setStateOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch states:", error);
      setStateOptions([
        { value: '', label: 'Select State', title: 'Select State', stateId: 0 }
      ]);
    } finally {
      setLoadingStates(false);
    }
  };

  // Fetch timezones from API
  const fetchTimezones = async () => {
    try {
      setLoadingTimezones(true);
      const response = await GetTimezoneType();
      
      if (response.isSuccess && Array.isArray(response.data) && response.data.length > 0) {
        const transformedTimezones = response.data.map(timezone => {
          const fullName = timezone.timeZoneName;
          // Truncate to 25 chars with ellipsis if longer
          const displayLabel = fullName.length > 25 
            ? fullName.slice(0, 25) + '...' 
            : fullName;
          
          return {
            value: timezone.timeZoneId.toString(),
            label: displayLabel,        // truncated for display
            title: fullName,            // full text shown on hover (tooltip)
            fullLabel: fullName,        // keep full version if needed
            timeZoneId: timezone.timeZoneId
          };
        });
        
        setTimezoneOptions(transformedTimezones);
      } else {
        console.warn("Timezone API response empty or invalid");
        setTimezoneOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch timezones:", error);
      setTimezoneOptions([
        { value: '', label: 'Select Time Zone', title: 'Select Time Zone', timeZoneId: 0 }
      ]);
    } finally {
      setLoadingTimezones(false);
    }
  };

  const fetchContactDetails = async () => {
    try {
      setLoadingContact(true);
      setError('');
      const response = await GetClientContact(clientId, contact.clientContactID);

      const isSuccess = response?.success === true || response?.isSuccess === true;

      if (isSuccess && response.data) {
        const contactData = response.data;
        let stateAbbr = '';
        if (contactData.contactStateID) {
          stateAbbr = contactData.contactStateID.toString();
        }
        
        if (contactData.state) {
          stateAbbr = contactData.state;
        }
        
        let timeZoneValue = contactData.timeZoneID?.toString() || '';
        
        const formData = {
          firstName: contactData.contactFirstName || contactData.firstName || '',
          lastName: contactData.contactLastName || contactData.lastName || '',
          jobTitle: contactData.contactPosition || contactData.jobTitle || '',
          timeZone: timeZoneValue,
          email: contactData.contactEmail || contactData.email || '',
          address: contactData.contactAddress || contactData.address || '',
          city: contactData.contactCity || contactData.city || '',
          state: stateAbbr,
          zipCode: contactData.contactZipCode || contactData.zipCode || '',
          phone: contactData.contactPhone || contactData.phone || ''
        };
        
        setInitialFormData(formData);
        setFormInitialized(true);
      } else {
        console.error('Invalid response from GetClientContact:', response);
        setError(response?.message || 'Failed to load contact details. API response invalid.');
        initializeFormData();
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
      setError(`Failed to load contact details: ${error.message}`);
      initializeFormData();
    } finally {
      setLoadingContact(false);
    }
  };

  // Initialize form with existing contact data or empty
  const initializeFormData = () => {
    if (isEditMode && contact) {
      const formData = {
        firstName: contact.firstName || contact.contactFirstName || '',
        lastName: contact.lastName || contact.contactLastName || '',
        jobTitle: contact.jobTitle || contact.contactPosition || '',
        timeZone: contact.timeZoneID?.toString() || contact.timeZone || '',
        email: contact.email || contact.contactEmail || '',
        address: contact.address || contact.contactAddress || '',
        city: contact.city || contact.contactCity || '',
        state: contact.state || contact.contactState || '',
        zipCode: contact.zipCode || contact.contactZipCode || '',
        phone: contact.phone || contact.contactPhone || ''
      };
      setInitialFormData(formData);
    } else {
      setInitialFormData({
        firstName: '',
        lastName: '',
        jobTitle: '',
        timeZone: '',
        email: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: ''
      });
    }
    setFormInitialized(true);
  };

  // Helper function to get state abbreviation from ID
  const getStateAbbreviation = (stateId) => {
    if (!stateId) return '';
    const state = stateOptions.find(s => s.stateId === parseInt(stateId));
    return state?.value || '';
  };

  // Helper function to get state ID from abbreviation
  const getStateIdFromAbbreviation = (stateAbbr) => {
    if (!stateAbbr) return 0;
    const state = stateOptions.find(s => s.value === stateAbbr);
    return state?.stateId || 0;
  };

  // Helper function to get timezone ID from value
  const getTimezoneIdFromValue = (timezoneValue) => {
    if (!timezoneValue) return 0;
    const timezone = timezoneOptions.find(t => t.value === timezoneValue);
    return timezone?.timeZoneId || 0;
  };

  // Helper to get tooltip text for currently selected timezone
  const getSelectedTimezoneTitle = () => {
    const selected = timezoneOptions.find(t => t.value === initialFormData.timeZone);
    return selected?.title || selected?.fullLabel || '';
  };

  // Helper to get tooltip text for currently selected state
  const getSelectedStateTitle = () => {
    const selected = stateOptions.find(s => s.value === initialFormData.state);
    return selected?.title || selected?.fullLabel || '';
  };

  const contactFields = [
    [
      {
        name: 'firstName',
        label: 'First Name',
        type: 'text',
        placeholder: 'Enter first name',
        required: true,
        className: 'form-group-half',
        validation: (value) => {
          if (!value || value.trim() === '') return 'First name is required';
          if (value.length < 2) return 'First name must be at least 2 characters';
          return null;
        }
      },
      {
        name: 'lastName',
        label: 'Last Name',
        type: 'text',
        placeholder: 'Enter last name',
        required: true,
        className: 'form-group-half',
        validation: (value) => {
          if (!value || value.trim() === '') return 'Last name is required';
          if (value.length < 2) return 'Last name must be at least 2 characters';
          return null;
        }
      }
    ],
    [
      {
        name: 'jobTitle',
        label: 'Job Title/Role',
        type: 'text',
        placeholder: 'Enter job title',
        className: 'form-group-half',
        validation: (value) => {
          if (value && value.length > 100) return 'Job title cannot exceed 100 characters';
          return null;
        }
      },
      {
        name: 'timeZone',
        label: 'Time Zone',
        type: 'select',
        options: timezoneOptions,
        loading: loadingTimezones,
        className: 'form-group-half',
        title: getSelectedTimezoneTitle(), // tooltip for selected value
        showOptionTitle: true,
        validation: (value) => {
          if (value && value !== '') {
            const isValidOption = timezoneOptions.some(option => option.value === value);
            if (!isValidOption) {
              return 'Please select a valid time zone';
            }
          }
          return null;
        },
        emptyMessage: timezoneOptions.length <= 1 ? 'No time zones available' : undefined
      }
    ],
    [
      {
        name: 'email',
        label: 'Email Address',
        type: 'email',
        placeholder: 'Enter email address',
        className: 'form-group-full',
        required: true,
        validation: (value) => {
          if (!value || value.trim() === '') return 'Email is required';
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) return 'Please enter a valid email address';
          return null;
        }
      }
    ],
    [
      {
        name: 'address',
        label: 'Street Address',
        type: 'text',
        placeholder: 'Enter street address',
        className: 'form-group-full',
        validation: (value) => {
          if (value && value.length > 200) return 'Address cannot exceed 200 characters';
          return null;
        }
      }
    ],
    [
      {
        name: 'city',
        label: 'City',
        type: 'text',
        placeholder: 'Enter city',
        className: 'form-group-third',
        validation: (value) => {
          if (value && value.length > 50) return 'City cannot exceed 50 characters';
          return null;
        }
      },
      {
        name: 'state',
        label: 'State',
        type: 'select',
        options: stateOptions,
        loading: loadingStates,
        className: 'form-group-third',
        title: getSelectedStateTitle(), // tooltip for selected value
        showOptionTitle: true,
        validation: (value) => {
          if (value && value !== '') {
            const isValidOption = stateOptions.some(option => option.value === value);
            if (!isValidOption) {
              return 'Please select a valid state';
            }
          }
          return null;
        },
        emptyMessage: stateOptions.length <= 1 ? 'No states available' : undefined
      },
      {
        name: 'zipCode',
        label: 'ZIP Code',
        type: 'text',
        placeholder: 'Enter ZIP code',
        className: 'form-group-third',
        validation: (value) => {
          if (value) {
            const zipRegex = /^\d{5}(-\d{4})?$/;
            if (!zipRegex.test(value)) return 'Please enter a valid ZIP code';
          }
          return null;
        }
      }
    ],
    [
      {
        name: 'phone',
        label: 'Phone Number',
        type: 'tel',
        placeholder: 'Enter phone number',
        className: 'form-group-full',
        validation: (value) => {
          if (value) {
            const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
            const cleaned = value.replace(/[^\d+]/g, '');
            if (!phoneRegex.test(cleaned)) return 'Please enter a valid phone number';
          }
          return null;
        }
      }
    ]
  ];

  const handleSave = async (formData) => {
    if (!clientId) {
      setError('Client ID is required to save contact');
      return;
    }

    if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!formData.email?.trim()) {
      setError('Email address is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const stateId = getStateIdFromAbbreviation(formData.state);
      const timeZoneId = getTimezoneIdFromValue(formData.timeZone);

      const apiPayload = {
        clientContactID: isEditMode && contact?.clientContactID ? parseInt(contact.clientContactID) : 0,
        clientId: parseInt(clientId),
        contactFirstName: formData.firstName.trim(),
        contactLastName: formData.lastName.trim(),
        contactPosition: formData.jobTitle?.trim() || '',
        contactPhone: formData.phone?.trim() || '',
        contactEmail: formData.email.trim(),
        contactAddress: formData.address?.trim() || '',
        contactCity: formData.city?.trim() || '',
        contactStateID: stateId,
        contactZipCode: formData.zipCode?.trim() || '',
        timeZoneID: timeZoneId
      };

      let apiResponse;
      
      if (isEditMode) {
        if (!contact?.clientContactID) {
          throw new Error('Contact ID is required for editing');
        }
        
        apiResponse = await UpdateClientContact(
          clientId,
          contact.clientContactID,
          apiPayload
        );
      } else {
        apiResponse = await CreateClientContact(apiPayload);
      }
      const isSuccess = true; //apiResponse?.success === true || apiResponse?.isSuccess === true;

      if (isSuccess) {
        const contactData = {
          ...formData,
          fullName: `${formData.firstName} ${formData.lastName}`.trim(),
          image: contact?.image || '../assets/contact-image.png',
          clientContactID: apiResponse.data?.clientContactID || (isEditMode ? contact.clientContactID : Date.now().toString()),
          contactFirstName: formData.firstName.trim(),
          contactLastName: formData.lastName.trim(),
          contactPosition: formData.jobTitle?.trim() || '',
          contactPhone: formData.phone?.trim() || '',
          contactEmail: formData.email.trim(),
          contactAddress: formData.address?.trim() || '',
          contactCity: formData.city?.trim() || '',
          contactStateID: stateId,
          contactZipCode: formData.zipCode?.trim() || '',
          timeZoneID: timeZoneId
        };

        onSave(contactData, isEditMode ? contact.id : null);
        window.location.reload();

        onClose();
      } else {
        setError(apiResponse?.message || `Failed to ${isEditMode ? 'update' : 'create'} contact`);
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      
      if (error.response) {
        const serverError = error.response.data;
        setError(serverError?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError(error.message || 'An unexpected error occurred');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) {
      if (window.confirm('Are you sure you want to cancel? Changes will not be saved.')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Show loading state while initializing
  if (isOpen && !formInitialized) {
    return (
      <CommonModal
        isOpen={isOpen}
        onClose={handleClose}
        onSave={() => {}}
        title={isEditMode ? "Edit Contact" : "Add New Contact"}
        fields={[]}
        initialData={{}}
        submitText="Loading..."
        cancelText="Cancel"
        width="650px"
        isLoading={true}
        error=""
        disableSubmit={true}
        customValidation={true}
      />
    );
  }

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleSave}
      title={isEditMode ? "Edit Contact" : "Add New Contact"}
      fields={contactFields}
      initialData={initialFormData}
      submitText={saving ? "Saving..." : (isEditMode ? "Update Contact" : "Add Contact")}
      cancelText="Cancel"
      width="650px"
      isLoading={saving}
      error={error}
      disableSubmit={saving || loadingStates || loadingTimezones || loadingContact}
      customValidation={true}
    />
  );
};

export default ContactModal;